import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import { getGraphCalendarToken, getGraphContactsToken } from './graphTokenHelper';
import {
    Building2, Users, TrendingUp, Bell, FileText,
    X, AlertCircle, Home, Menu, Shield, Settings,
    LogOut, ClipboardList, User, Upload, Search, FileSignature
} from 'lucide-react';
import './crm.css';
import './investor-dashboard.css';

import { API_BASE, capitalizeFirst } from './components/helpers';
import { DashboardView, ContactsView, LeadsView, RemindersView, ContractsView, PropertiesView } from './components/views';
import FormModal from './components/FormModal';
import DetailModal from './components/DetailModal';
import ConvertWizard from './components/ConvertWizard';
import ImportTool from './components/ImportTool';
import UsersManagement from './components/UsersManagement';
import { InvestorLoginScreen, InvestorDashboard } from './components/InvestorPortal';
import DMShell from './components/dm/DMShell';

// Lazy import keeps the signing chunk (incl. react-pdf, react-signature-canvas)
// out of the CRM/DM bundles. Vendors only load it when they visit /sign/<token>.
// Declared at module scope — NOT inside AppRoot — so the lazy component is
// created exactly once. AppRoot is wrapped in <MsalProvider>/<StrictMode>
// (see index.js) and re-renders; a per-render React.lazy() would produce a
// new component identity each time and remount SignPage, discarding the
// vendor's in-progress signature/typed name/consent.
const LazySignPage = React.lazy(() => import('./components/sign/SignPage'));

const AuthContext = React.createContext(null);

function useAuth() {
  return React.useContext(AuthContext);
}

// Set up axios interceptor to attach Bearer token (ID token) to API requests
function useAxiosInterceptor() {
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const interceptor = axios.interceptors.request.use(async (config) => {
      // Skip token for investor API calls
      if (config.url && config.url.includes('/api/investor')) {
        return config;
      }

      if (accounts.length > 0) {
        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          config.headers.Authorization = `Bearer ${response.idToken}`;
        } catch (err) {
          console.error('Failed to acquire token silently:', err);
        }
      }
      return config;
    });

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [instance, accounts]);
}

function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [deniedEmail, setDeniedEmail] = useState('');

  useAxiosInterceptor();

  const fetchUserProfile = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Get token first, then call /me
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      const res = await axios.get(`${API_BASE}/users/me`, {
        headers: { Authorization: `Bearer ${tokenResponse.idToken}` },
      });

      setUser(res.data);
      setAccessDenied(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
        setDeniedEmail(accounts[0]?.username || '');
        setUser(null);
      } else {
        console.error('Failed to fetch user profile:', err);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, accounts, instance]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      fetchUserProfile();
    }
  }, [isAuthenticated, inProgress, fetchUserProfile]);

  const logout = () => {
    instance.logoutRedirect();
  };

  return (
    <AuthContext.Provider value={{ user, logout, loading, accessDenied, deniedEmail }}>
      {children}
    </AuthContext.Provider>
  );
}

function LoginScreen() {
  const { instance } = useMsal();
  const [showInvestorLogin, setShowInvestorLogin] = useState(false);

  if (showInvestorLogin) {
    return <InvestorLoginScreen onBack={() => setShowInvestorLogin(false)} />;
  }

  const handleLogin = (intent) => {
    sessionStorage.setItem('dm_signin_intent', intent);
    instance.loginRedirect(loginRequest);
  };

  return (
    <div className="crm-login">
      <div className="crm-login__card">
        <div className="crm-login__logo">
          <img src="/cagan-logo.png" alt="Cagan Logo" className="crm-login__logo-img" />
        </div>
        <h1 className="crm-login__title">Cagan Management Group</h1>
        <p className="crm-login__subtitle">Property Management System</p>

        <div className="crm-login__divider" />

        <button className="crm-login__btn crm-login__btn--admin" onClick={() => handleLogin('crm')}>
          <Shield size={18} />
          CRM Login
        </button>

        <button className="crm-login__btn crm-login__btn--dm" onClick={() => handleLogin('dm')}>
          <FileSignature size={18} />
          Document Manager Login
        </button>

        <div className="crm-login__divider" />

        <button className="crm-login__btn crm-login__btn--investor" onClick={() => setShowInvestorLogin(true)}>
          <Building2 size={18} />
          Client Portal Login
        </button>
      </div>
    </div>
  );
}

function AccessDenied() {
  const { deniedEmail } = useAuth();
  const { instance } = useMsal();

  return (
    <div className="crm-login">
      <div className="crm-login__card">
        <div className="crm-login__logo">
          <img src="/cagan-logo.png" alt="Cagan Logo" className="crm-login__logo-img" />
        </div>
        <h1 className="crm-login__title">Access Denied</h1>
        <p className="crm-login__subtitle">
          Your account ({deniedEmail}) has not been granted access to this system. Contact your administrator.
        </p>
        <div className="crm-login__divider" />
        <button className="crm-login__btn crm-login__btn--user" onClick={() => instance.logoutRedirect()}>
          <LogOut size={18} />
          Sign Out
        </button>
      </div>
    </div>
  );
}

function NoAccess({ app }) {
  const { instance } = useMsal();

  return (
    <div className="crm-login">
      <div className="crm-login__card">
        <div className="crm-login__logo">
          <img src="/cagan-logo.png" alt="Cagan Logo" className="crm-login__logo-img" />
        </div>
        <h1 className="crm-login__title">No Access</h1>
        <p className="crm-login__subtitle">
          You don't have access to the {app}. Please contact your administrator if you believe this is a mistake.
        </p>
        <div className="crm-login__divider" />
        <button className="crm-login__btn crm-login__btn--admin" onClick={() => instance.logoutRedirect()}>
          <LogOut size={18} />
          Back to Login
        </button>
        <button className="crm-login__btn crm-login__btn--user" onClick={() => instance.logoutRedirect()}>
          <LogOut size={18} />
          Sign Out
        </button>
      </div>
    </div>
  );
}

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [showConvertWizard, setShowConvertWizard] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [pendingReminderDefaults, setPendingReminderDefaults] = useState(null);
  const [users, setUsers] = useState([]);
  const { user, logout } = useAuth();
  const { instance: msalInstance, accounts: msalAccounts } = useMsal();
  const bulkCancelRef = useRef(false);

  // Acquire Graph calendar token for Outlook sync (best-effort, returns {} if unavailable)
  const getCalendarHeaders = async () => {
    try {
      const token = await getGraphCalendarToken(msalInstance, msalAccounts[0]);
      return token ? { 'X-Graph-Token': token } : {};
    } catch {
      return {};
    }
  };

  // Bulk push all active contacts to Outlook (client-side iteration for progress)
  const bulkPushToOutlook = async (setBulkPushing, setBulkProgress) => {
    bulkCancelRef.current = false;
    setBulkPushing(true);
    const activeContacts = contacts.filter(c => c.Status !== 'Inactive' && !c.IsArchived);
    const total = activeContacts.length;
    setBulkProgress({ current: 0, total, succeeded: 0, failed: 0, cancelled: false });

    try {
      const token = await getGraphContactsToken(msalInstance, msalAccounts[0]);
      if (!token) {
        alert('Could not acquire Outlook permission. Please allow the consent popup.');
        setBulkPushing(false);
        setBulkProgress(null);
        return;
      }

      let succeeded = 0;
      let failed = 0;

      for (let i = 0; i < activeContacts.length; i++) {
        if (bulkCancelRef.current) {
          setBulkProgress({ current: i, total, succeeded, failed, cancelled: true });
          break;
        }
        try {
          const res = await axios.post(
            `${API_BASE}/contacts/${activeContacts[i].ContactId}/push-to-outlook`,
            {},
            { headers: { 'X-Graph-Token': token } }
          );
          if (res.data.success) succeeded++;
          else failed++;
        } catch {
          failed++;
        }
        setBulkProgress({ current: i + 1, total, succeeded, failed, cancelled: false });

        // 200ms delay between calls to avoid Graph rate limits
        if (i < activeContacts.length - 1 && !bulkCancelRef.current) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    } catch (err) {
      console.error('Bulk push failed:', err);
    } finally {
      setBulkPushing(false);
    }
  };

  // Close admin dropdown when clicking outside
  useEffect(() => {
    if (!adminMenuOpen) return;
    const handleClick = (e) => {
      if (!e.target.closest('.crm-admin-dropdown')) setAdminMenuOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [adminMenuOpen]);

  // Fetch data on tab change OR when lastUpdate changes
  useEffect(() => {
    setSearchTerm('');
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, lastUpdate]);

  useEffect(() => {
    axios.get(`${API_BASE}/options`)
      .then(res => setDropdownOptions(res.data))
      .catch(err => console.error('Error loading dropdown options:', err));
    axios.get(`${API_BASE}/options/users/list`)
      .then(res => setUsers(res.data))
      .catch(err => console.error('Error loading users:', err));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      switch (activeTab) {
        case 'dashboard':
          const statsRes = await axios.get(`${API_BASE}/dashboard/stats`);
          setDashboardStats(statsRes.data);
          const remindersRes = await axios.get(`${API_BASE}/reminders/upcoming`);
          setUpcomingReminders(remindersRes.data);
          break;
        case 'contacts':
          const contactsRes = await axios.get(`${API_BASE}/contacts`);
          setContacts(contactsRes.data);
          break;
        case 'leads':
          const [leadsRes, leadsContactsRes] = await Promise.all([
            axios.get(`${API_BASE}/leads`),
            axios.get(`${API_BASE}/contacts`)
          ]);
          setLeads(leadsRes.data);
          setContacts(leadsContactsRes.data);
          break;
        case 'reminders':
          const [allRemindersRes, remContactsRes, remLeadsRes, remPropsRes, remContractsRes] = await Promise.all([
            axios.get(`${API_BASE}/reminders`),
            axios.get(`${API_BASE}/contacts`),
            axios.get(`${API_BASE}/leads`),
            axios.get(`${API_BASE}/properties`),
            axios.get(`${API_BASE}/contracts`)
          ]);
          setReminders(allRemindersRes.data);
          setContacts(remContactsRes.data);
          setLeads(remLeadsRes.data);
          setProperties(remPropsRes.data);
          setContracts(remContractsRes.data);
          break;
        case 'contracts':
          const [contractsRes, contractContactsRes] = await Promise.all([
            axios.get(`${API_BASE}/contracts`),
            axios.get(`${API_BASE}/contacts`)
          ]);
          setContracts(contractsRes.data);
          setContacts(contractContactsRes.data);
          break;
        case 'properties':
          const [propertiesRes, propsContactsRes] = await Promise.all([
            axios.get(`${API_BASE}/properties`),
            axios.get(`${API_BASE}/contacts`)
          ]);
          setProperties(propertiesRes.data);
          setContacts(propsContactsRes.data);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType(null);
    setSelectedItem(null);
  };

  const handleSave = async (data) => {
    try {
      const endpoint = modalType === 'property' ? 'properties' : `${modalType}s`;
      const calHeaders = modalType === 'reminder' ? await getCalendarHeaders() : {};
      // Lead property picks (create mode) ride along in `pendingProperties`;
      // keep them out of the entity payload sent to the API.
      const { pendingProperties, ...payload } = data;
      let newId = null;
      let propertyLinkWarning = null;
      if (selectedItem) {
        // Update
        await axios.put(`${API_BASE}/${endpoint}/${selectedItem[`${capitalizeFirst(modalType)}Id`]}`, payload, { headers: calHeaders });
        newId = selectedItem[`${capitalizeFirst(modalType)}Id`];
      } else {
        // Create
        const res = await axios.post(`${API_BASE}/${endpoint}`, payload, { headers: calHeaders });
        newId = res.data.leadId || res.data.contactId || res.data.propertyId || res.data.contractId || res.data.reminderId;

        // Link any properties picked while creating the lead. The lead row only
        // exists now, so this is the earliest the links can be written. A link
        // that fails must not be swallowed — track which ones so the user can
        // re-add them rather than silently losing them.
        if (modalType === 'lead' && newId && Array.isArray(pendingProperties) && pendingProperties.length > 0) {
          const failed = [];
          for (const prop of pendingProperties) {
            try {
              await axios.post(`${API_BASE}/leads/${newId}/properties`, { propertyId: prop.PropertyId });
            } catch (linkErr) {
              if (linkErr.response && linkErr.response.status === 409) continue; // already linked — fine
              failed.push(prop);
            }
          }
          if (failed.length > 0) {
            const names = failed.map(p => p.PropertyName).join(', ');
            propertyLinkWarning = `Lead created, but ${failed.length === 1 ? 'a property' : `${failed.length} properties`} could not be linked: ${names}. Open the lead to add ${failed.length === 1 ? 'it' : 'them'}.`;
          }
        }
      }
      closeModal();
      closeDetail();
      fetchData();
      // fetchData() clears `error`; surface the partial-link warning after it so it persists.
      if (propertyLinkWarning) setError(propertyLinkWarning);

      // If lead was saved with a follow-up date, open reminder form pre-populated
      if (modalType === 'lead' && data.nextFollowUpDate && newId) {
        setPendingReminderDefaults({
          title: `Follow up: ${data.propertyInterest || 'Lead'}`,
          dueDate: data.nextFollowUpDate,
          reminderType: 'Follow Up',
          priority: 'Medium',
          relatedToType: 'Lead',
          relatedToId: String(newId)
        });
        setTimeout(() => {
          openModal('reminder');
        }, 100);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setError(err.response.data.error);
      } else {
        setError(err.message);
      }
    }
  };

  const handleDelete = async (type, id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const endpoint = type === 'property' ? 'properties' : `${type}s`;
        const calHeaders = type === 'reminder' ? await getCalendarHeaders() : {};
        await axios.delete(`${API_BASE}/${endpoint}/${id}`, { headers: calHeaders });
        fetchData();
        setLastUpdate(Date.now()); // Trigger dashboard refresh
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    }
  };

  const archiveItem = async (type, id) => {
  if (window.confirm('Are you sure you want to archive this item?')) {
    try {
      const endpoint = type === 'property' ? 'properties' : `${type}s`;
      const calHeaders = type === 'reminder' ? await getCalendarHeaders() : {};
      await axios.put(`${API_BASE}/${endpoint}/${id}/archive`, {}, { headers: calHeaders });
      fetchData();
      setLastUpdate(Date.now()); // Trigger dashboard refresh
    } catch (err) {
      setError(err.message);
    }
  }
};

  const completeReminder = async (id) => {
    try {
      const calHeaders = await getCalendarHeaders();
      await axios.put(`${API_BASE}/reminders/${id}/complete`, {}, { headers: calHeaders });
      fetchData();
      setLastUpdate(Date.now()); // Trigger dashboard refresh
    } catch (err) {
      setError(err.message);
    }
  };

    const openDetail = (type, item) => {
            setDetailType(type);
            setDetailItem(item);
            setShowDetailModal(true);
          };

          const closeDetail = () => {
               setShowDetailModal(false);
               setDetailItem(null);
               setDetailType(null);
             };

  const openConvertWizard = (lead) => {
    setConvertLead(lead);
    setShowConvertWizard(true);
  };

  const closeConvertWizard = () => {
    setShowConvertWizard(false);
    setConvertLead(null);
  };

  const handleConvert = async (leadId, contractData) => {
    try {
      await axios.post(`${API_BASE}/leads/${leadId}/convert`, contractData);
      closeConvertWizard();
      fetchData();
      setLastUpdate(Date.now());
    } catch (err) {
      if (err.response?.status === 409) {
        setError(err.response.data.error);
      } else {
        setError(err.message);
      }
    }
  };

  return (
    <div className="crm-page">
      {/* Mobile top bar */}
      <div className="crm-topbar">
        <button className="crm-topbar__toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <span className="crm-topbar__title">Cagan Management Group</span>
      </div>

      {/* Sidebar overlay for mobile */}
      {mobileMenuOpen && <div className="crm-sidebar-overlay" onClick={() => setMobileMenuOpen(false)} />}

      {/* Sidebar */}
      <aside className={`crm-sidebar ${mobileMenuOpen ? 'crm-sidebar--open' : ''}`}>
        <nav className="crm-sidebar__nav">
          {[
            { id: 'dashboard', icon: Home, label: 'Dashboard' },
            { id: 'contacts', icon: Users, label: 'Contacts' },
            { id: 'properties', icon: Building2, label: 'Properties' },
            { id: 'leads', icon: TrendingUp, label: 'Leads' },
            { id: 'reminders', icon: Bell, label: 'Reminders' },
            { id: 'contracts', icon: FileText, label: 'Contracts' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setMobileMenuOpen(false);
              }}
              className={`crm-sidebar__link ${activeTab === tab.id ? 'crm-sidebar__link--active' : ''}`}
            >
              <tab.icon size={18} className="crm-sidebar__link-icon" />
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Apps switcher — only for users with Document Manager access */}
        {user && user.hasDMAccess === true && (
          <div className="crm-sidebar__section">
            <div className="crm-sidebar__section-label">Apps</div>
            <button
              type="button"
              onClick={() => { window.location.href = '/dm/envelopes'; }}
              className="crm-sidebar__link"
            >
              <FileSignature size={18} className="crm-sidebar__link-icon" />
              Document Manager
            </button>
          </div>
        )}

        {/* Admin section — only visible to admin users */}
        {user && user.role === 'admin' && (
          <div className="crm-sidebar__section">
            <div className="crm-sidebar__section-label">Admin</div>
            {[
              { id: 'admin-import', icon: Upload, label: 'Import Data' },
              { id: 'admin-users', icon: Users, label: 'Manage Users' },
              { id: 'admin-settings', icon: Settings, label: 'System Settings' },
              { id: 'admin-audit', icon: ClipboardList, label: 'Audit Log' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setAdminMenuOpen(false);
                  setMobileMenuOpen(false);
                }}
                className={`crm-sidebar__link ${activeTab === tab.id ? 'crm-sidebar__link--active' : ''}`}
              >
                <tab.icon size={18} className="crm-sidebar__link-icon" />
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* User info & logout at bottom */}
        <div className="crm-sidebar__footer">
          <div className="crm-sidebar__user">
            <div className="crm-sidebar__user-avatar">
              <User size={16} />
            </div>
            <div className="crm-sidebar__user-info">
              <span className="crm-sidebar__user-name">{user?.name}</span>
              <span className="crm-sidebar__user-role">{user?.role === 'admin' ? 'Administrator' : 'User'}</span>
            </div>
            <button className="crm-sidebar__logout" onClick={logout} data-tooltip="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="crm-main">
        {/* Top Bar */}
        <div className="crm-topheader">
          <div className="crm-topheader__left">
            {['contacts', 'properties', 'leads', 'reminders', 'contracts'].includes(activeTab) && (
              <div className="crm-topheader__search">
                <Search size={18} className="crm-topheader__search-icon" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="crm-topheader__search-input"
                />
              </div>
            )}
          </div>
          <div className="crm-topheader__right">
            <div className="crm-topheader__brand-text">
              <span className="crm-topheader__brand-title">Cagan Management Group</span>
              <span className="crm-topheader__brand-subtitle">CRM</span>
            </div>
            <div className="crm-topheader__logo">
              <img src="/cagan-logo.png" alt="Cagan Logo" className="crm-topheader__logo-img" />
            </div>
          </div>
        </div>

        <div className="crm-main__content">
        {error && (
          <div className="crm-error">
            <AlertCircle size={20} className="crm-error__icon" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="crm-loading">
            <div className="crm-loading__container">
              <div className="crm-loading__spinner" />
              <p className="crm-loading__text">Loading...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <DashboardView
                stats={dashboardStats}
                upcomingReminders={upcomingReminders}
                onCompleteReminder={completeReminder}
                onNavigate={setActiveTab}
              />
            )}
            {activeTab === 'contacts' && (
			 <ContactsView
			 contacts={contacts}
			 searchTerm={searchTerm}
			 onSearch={setSearchTerm}
			 onAdd={() => openModal('contact')}
			 onEdit={(item) => openModal('contact', item)}
			 onDelete={(id) => handleDelete('contact', id)}
             onArchive={(id) => archiveItem('contact', id)}
             onViewDetail={(contact) => openDetail('contact', contact)}
             onBulkPushToOutlook={bulkPushToOutlook}
             bulkCancelRef={bulkCancelRef}
			 />
			)}
            {activeTab === 'properties' && (
             <PropertiesView
               properties={properties}
               searchTerm={searchTerm}
               onSearch={setSearchTerm}
               onAdd={() => openModal('property')}
               onEdit={(item) => openModal('property', item)}
               onDelete={(id) => handleDelete('property', id)}
               onArchive={(id) => archiveItem('property', id)}
               onViewDetail={(property) => openDetail('property', property)}
             />
            )}
            {activeTab === 'leads' && (
			 <LeadsView
			 leads={leads}
			 searchTerm={searchTerm}
			 onSearch={setSearchTerm}
			 onAdd={() => openModal('lead')}
			 onEdit={(item) => openModal('lead', item)}
			 onDelete={(id) => handleDelete('lead', id)}
             onArchive={(id) => archiveItem('lead', id)}
             onViewDetail={(lead) => openDetail('lead', lead)}
             onConvert={(lead) => openConvertWizard(lead)}
			  />
			)}
{activeTab === 'reminders' && (
  <RemindersView
    reminders={reminders}
    searchTerm={searchTerm}
    onSearch={setSearchTerm}
    onAdd={() => openModal('reminder')}
    onEdit={(item) => openModal('reminder', item)}
    onDelete={(id) => handleDelete('reminder', id)}
    onComplete={completeReminder}
    onArchive={(id) => archiveItem('reminder', id)}
  />
)}
{activeTab === 'contracts' && (
  <ContractsView
    contracts={contracts}
    searchTerm={searchTerm}
    onSearch={setSearchTerm}
    onAdd={() => openModal('contract')}
    onEdit={(item) => openModal('contract', item)}
    onDelete={(id) => handleDelete('contract', id)}
    onArchive={(id) => archiveItem('contract', id)}
    onViewDetail={(contract) => openDetail('contract', contract)}
  />
)}
{activeTab === 'admin-import' && <ImportTool />}
{activeTab === 'admin-users' && <UsersManagement />}
{activeTab === 'admin-settings' && <AdminPlaceholder title="System Settings" icon={Settings} description="Configure system-wide settings and preferences." />}
{activeTab === 'admin-audit' && <AdminPlaceholder title="Audit Log" icon={ClipboardList} description="View system activity and change history." />}
          </>
        )}
        </div>
      </main>

      {/* Detail Modal */}
      {showDetailModal && detailItem && (
        <DetailModal
          type={detailType}
          item={detailItem}
          onClose={closeDetail}
          onEdit={(editItem) => { openModal(detailType, editItem); }}
          dropdownOptions={dropdownOptions}
          onNavigate={(navType, navItem) => openDetail(navType, navItem)}
          users={users}
          contacts={contacts}
        />
          )}

      {/* Modal */}
      {showModal && (
        <FormModal
          type={modalType}
          item={selectedItem}
          defaultData={pendingReminderDefaults}
          onClose={() => { closeModal(); setPendingReminderDefaults(null); }}
          onSave={handleSave}
          dropdownOptions={dropdownOptions}
          contacts={contacts}
          entities={{ contacts, leads, properties, contracts }}
          users={users}
          onContactCreated={async () => {
            const res = await axios.get(`${API_BASE}/contacts`);
            setContacts(res.data);
            return res.data;
          }}
        />
          )}

      {/* Convert Wizard */}
      {showConvertWizard && convertLead && (
        <ConvertWizard
          lead={convertLead}
          onClose={closeConvertWizard}
          onConvert={handleConvert}
          dropdownOptions={dropdownOptions}
        />
      )}
    </div>
  );
}


// Dashboard View Component
function AdminPlaceholder({ title, icon: Icon, description }) {
  return (
    <div className="crm-admin-placeholder">
      <div className="crm-admin-placeholder__icon">
        <Icon size={48} />
      </div>
      <h2 className="crm-admin-placeholder__title">{title}</h2>
      <p className="crm-admin-placeholder__description">{description}</p>
      <p className="crm-admin-placeholder__coming-soon">Coming Soon</p>
    </div>
  );
}

// Root wrapper — handles auth gate
function AppRoot() {
  // Public signing page — short-circuits BEFORE AuthProvider so MSAL is
  // never initialized for signers (who don't have Azure AD accounts).
  // Refreshing on /sign/<token> stays on the signing page; refreshing on
  // any other path behaves exactly as before this change.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/sign/')) {
    const token = window.location.pathname.slice('/sign/'.length).split('/')[0];
    if (token) {
      return (
        <React.Suspense fallback={
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f8f6' }}>
            <div style={{ textAlign: 'center', color: '#78716c', fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif' }}>
              Loading…
            </div>
          </div>
        }>
          <LazySignPage token={token} />
        </React.Suspense>
      );
    }
  }

  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

function AuthGate() {
  const { user, loading, accessDenied, logout } = useAuth();
  const { inProgress } = useMsal();
  // Capture the post-login landing intent exactly once at mount. Reading
  // sessionStorage inside the render body races with React 18 <StrictMode>
  // double-invocation AND AuthGate's natural re-renders during the auth
  // sequence (MSAL inProgress flips, AuthProvider setLoading/setUser): the
  // first render reads 'dm' and removes the key; every later render sees
  // null and falls through to the URL-path rule below — which after an
  // OAuth redirect to "/" wrongly resolves a Document Manager sign-in as
  // CRM. The lazy useState initializer reads + removes in a single step at
  // mount; subsequent renders use the captured state. Clearing the key on
  // capture preserves the prior semantic where later hard refreshes fall
  // back to the URL rule.
  const [intent] = useState(() => {
    const value = sessionStorage.getItem('dm_signin_intent');
    if (value) sessionStorage.removeItem('dm_signin_intent');
    return value;
  });

  // Check if logged in as investor (via sessionStorage JWT)
  const investorToken = sessionStorage.getItem('investorToken');
  if (investorToken) {
    return <InvestorDashboard />;
  }

  // MSAL is handling a redirect
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="crm-loading">
        <div className="crm-loading__container">
          <div className="crm-loading__spinner" />
          <p className="crm-loading__text">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Loading CRM profile from backend
  if (loading) {
    return (
      <div className="crm-loading">
        <div className="crm-loading__container">
          <div className="crm-loading__spinner" />
          <p className="crm-loading__text">Loading...</p>
        </div>
      </div>
    );
  }

  // Authenticated with Azure AD but not authorized in CRM
  if (accessDenied) {
    return <AccessDenied />;
  }

  // Not authenticated
  if (!user) return <LoginScreen />;

  // Decide CRM vs DM. The intent captured at mount (see useState above)
  // wins on the first load after sign-in. Because it was cleared from
  // sessionStorage at capture time, any later refresh falls back to the
  // URL-path rule below — this keeps a /dm/... URL in the Document Manager
  // across refreshes instead of silently dropping back to the CRM.
  let target;
  if (intent === 'dm') {
    target = 'dm';
  } else if (intent === 'crm') {
    target = 'crm';
  } else {
    target = window.location.pathname.startsWith('/dm') ? 'dm' : 'crm';
  }

  // Enforce access for the chosen target.
  if (target === 'dm') {
    return user.hasDMAccess
      ? <DMShell user={user} onSignOut={logout} />
      : <NoAccess app="Document Manager" />;
  }

  // target === 'crm' (hasCRMAccess defaults to true for existing users)
  return user.hasCRMAccess !== false
    ? <App />
    : <NoAccess app="CRM" />;
}

export default AppRoot;
